/**
 * DOCUMENTATION:
 * Component: SystemDesignCDN
 * Purpose: Teaching Content Delivery Networks and Edge Computing.
 * * * Update Note: Fixed LaTeX rendering using react-katex.
 * Standard MUI Typography cannot parse complex math strings natively.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

/* ✅ Math Rendering Imports */
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

/* Icons */
import PublicIcon from '@mui/icons-material/Public';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import SecurityIcon from '@mui/icons-material/Security';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

export default function SystemDesignCDN() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    GLOBAL EDGE COMPUTING
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. What is a CDN? */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><PublicIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>The Content Delivery Network</Typography>
                        </Box>
                        <Typography mb={4} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            A <strong>CDN</strong> is a distributed network of servers (POPs) that work together to provide fast delivery of Internet content.
                            It stores a cached version of your website's content in multiple geographical locations. When a user in London
                            requests your site, the CDN serves it from a London server instead of your "Origin" server in California.
                        </Typography>

                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} p={2} bgcolor="rgba(255,255,255,0.03)" borderRadius="8px">
                                    <SpeedIcon sx={{ color: "#00c8ff" }} />
                                    <Box>
                                        <Typography fontWeight={700}>Reduced Latency</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>By shortening the physical distance data travels, we drastically improve load times.</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} p={2} bgcolor="rgba(255,255,255,0.03)" borderRadius="8px">
                                    <SecurityIcon sx={{ color: "#00c8ff" }} />
                                    <Box>
                                        <Typography fontWeight={700}>DDoS Protection</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>CDNs act as a shield, absorbing massive traffic spikes and malicious attacks before they reach your Origin.</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Edge Computing */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#a78bfa" }}><CloudCircleIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Edge Computing & Serverless</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            The "Edge" isn't just for storage anymore. With <strong>Edge Functions</strong>, you can run logic
                            at the POP level. This allows for hyper-fast personalization, A/B testing, and security checks
                            without ever hitting your main server.
                        </Typography>

                        {/* ✅ Latency Math Section - Now correctly rendered */}
                        <Box sx={{ p: 3, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", mt: 2 }}>
                            <Typography variant="subtitle1" color="#a78bfa" fontWeight={700} gutterBottom>Latency Math:</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                If a signal travels 5,000 miles (California to London), the minimum physical delay (latency) is approximately:
                            </Typography>

                            <Box sx={{ textAlign: 'center', py: 2, color: 'goldenrod' }}>
                                {/* Use BlockMath for standalone centered equations */}
                                <BlockMath math="T \approx \frac{D}{c \times 0.66}" />

                                <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 1 }}>
                                    Where $D$ is distance and $c$ is the speed of light in fiber.
                                    By using a CDN, we reduce $D$ from 5,000 miles to 50 miles.
                                </Typography>
                            </Box>
                        </Box>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
